import {
  GitMergeIcon,
  GitMergeQueueIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  IssueClosedIcon,
  IssueOpenedIcon,
  SkipIcon,
} from '@primer/octicons-react'

import type {AppPayload} from '../types/app-payload'

const IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES_ENUM = {
  public: 'public',
  private: 'private',
} as const

const IS_FILTER_PROVIDER_VALUES_ENUM = {
  closed: 'closed',
  draft: 'draft',
  issue: 'issue',
  locked: 'locked',
  merged: 'merged',
  unmerged: 'unmerged',
  open: 'open',
  pr: 'pr',
  unlocked: 'unlocked',
  blocked: 'blocked',
  blocking: 'blocking',
} as const

type IS_FILTER_PROVIDER_VALUES_ENUM =
  | (typeof IS_FILTER_PROVIDER_VALUES_ENUM)[keyof typeof IS_FILTER_PROVIDER_VALUES_ENUM]
  | (typeof IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES_ENUM)[keyof typeof IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES_ENUM]

const STATE_FILTER_PROVIDER_VALUES_ENUM = {
  open: 'open',
  closed: 'closed',
  draft: 'draft',
  merged: 'merged',
} as const

type STATE_FILTER_PROVIDER_VALUES_ENUM =
  (typeof STATE_FILTER_PROVIDER_VALUES_ENUM)[keyof typeof STATE_FILTER_PROVIDER_VALUES_ENUM]

const MAX_ES_ISSUES = 50000
const MAX_ES_PRS = 10000
const MAX_ES_DEFAULT = 1000

type IS_FILTER_PROVIDER_VALUES = Array<keyof typeof IS_FILTER_PROVIDER_VALUES_ENUM>

/**
 * Default values for the IsFilterProvider used within issues
 */
export const IS_FILTER_PROVIDER_VALUES: IS_FILTER_PROVIDER_VALUES = Object.values(IS_FILTER_PROVIDER_VALUES_ENUM)

type IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES = Array<
  keyof typeof IS_FILTER_PROVIDER_VALUES_ENUM | keyof typeof IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES_ENUM
>

/**
 * A superset of values for the IsFilterProvider used within the issues dashboard, including repository and issue scope
 */
const IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES: IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES = [
  ...Object.values(IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES_ENUM),
  ...IS_FILTER_PROVIDER_VALUES,
]

export const STATE_FILTER_PROVIDER_VALUES: Array<keyof typeof STATE_FILTER_PROVIDER_VALUES_ENUM> = Object.values(
  STATE_FILTER_PROVIDER_VALUES_ENUM,
)

export const VALUES = {
  repositoriesPreloadCount: 5,
  issuesPageSizeDefault: 25,
  issuesPageSizeMax: 100,
  issuesPageSize: (search: string) => {
    // Callers in render scope should pass useLocation().search so the page size
    // reacts to URL changes.
    const urlSearchParams = new URLSearchParams(search)

    const pageSize = urlSearchParams.get('pageSize')
    return Math.min(pageSize ? parseInt(pageSize) : 25, VALUES.issuesPageSizeMax)
  },
  pullRequestsMaxPageSize: 50,
  labelPageSize: 20,
  assigneePageSize: 20,
  viewsPageSize: 25,
  viewLoadingSize: 6,
  teamsMaxPreloadCount: 100,
  teamsPageSize: 25,
  searchUrlParameterName: 'q',
  maxBulkUpdateIssues: 1000,
  // Limitation enforced by Elasticsearch
  maxIssuesListItems: (isPrsOnly: boolean, loggedIn: boolean) => {
    if (loggedIn) {
      return isPrsOnly ? MAX_ES_PRS : MAX_ES_ISSUES
    } else {
      return MAX_ES_DEFAULT
    }
  },
  localStorageKeyBulkUpdateIssues: 'repo.bulkUpdateIssues',
  storageKeyPrefix: (appPayload: AppPayload) => appPayload.scoped_repository?.id || 'hyperlist',
  defaultViewIcon: 'BOOKMARK',
  defaultViewColor: 'GRAY',
  defaultQueryForNewView: '',
  issueIcons: {
    OPEN: {
      color: 'var(--fgColor-open)',
      icon: IssueOpenedIcon,
      description: 'Open issue',
    },
    CLOSED: {
      color: 'var(--fgColor-done)',
      icon: IssueClosedIcon,
      description: 'Closed issue (completed)',
    },
    NOT_PLANNED: {
      color: 'var(--fgColor-muted)',
      icon: SkipIcon,
      description: 'Closed issue (not planned)',
    },
  },
  serviceOwners: {
    notification: 'github/notifications',
    pullRequests: 'github/pull_requests',
    hyperlist: 'github/hyperlist_web',
  },
  pullRequestIcons: {
    MERGED: {
      color: 'var(--fgColor-done)',
      icon: GitMergeIcon,
    },
    IN_MERGE_QUEUE: {
      color: 'var(--fgColor-attention)',
      icon: GitMergeQueueIcon,
    },
    OPEN: {
      color: 'var(--fgColor-open)',
      icon: GitPullRequestIcon,
    },
    CLOSED: {
      color: 'var(--fgColor-closed)',
      icon: GitPullRequestClosedIcon,
    },
    DRAFT: {
      color: 'var(--fgColor-draft)',
      icon: GitPullRequestDraftIcon,
    },
  },
}
